<script setup lang="ts">
    import MobileBackground from '~/assets/images/academy-background.svg';
    import ProductOfTheMonthMobileBackground from '~/assets/images/potm_background_mobile.svg';
    import Bee from '~/assets/icons/bee.svg';
    import { gsap } from 'gsap';

    type BackgroundVariant = 'academy' | 'productOfTheMonth' | 'training';

    const props = withDefaults(
        defineProps<{
            variant?: BackgroundVariant;
        }>(),
        {
            variant: 'academy'
        }
    );

    const uniqueId = useId().replaceAll(':', '');

    const backgroundVariants: Record<
        BackgroundVariant,
        {
            mobileBackground: string;
            showBee: boolean;
            colors: {
                circleMain: string;
                circleBackRight: string;
                orangeGradient: string;
                blueGradient: string;
                tertiary: string;
                tertiaryOpacity: number;
            };
            gradientStops: {
                orange: {
                    startOffset: string;
                    startOpacity: number;
                    middleOffset: string;
                    middleOpacity: number;
                    endOffset: string;
                    endOpacity: number;
                };
                blue: {
                    startOffset: string;
                    startOpacity: number;
                    middleOffset: string;
                    middleOpacity: number;
                    whiteOffset: string;
                    whiteOpacity: number;
                    endOffset: string;
                    endOpacity: number;
                };
            };
        }
    > = {
        academy: {
            mobileBackground: MobileBackground,
            showBee: true,
            colors: {
                circleMain: '#B80E80',
                circleBackRight: '#B80E80',
                orangeGradient: '#FF9800',
                blueGradient: '#007F95',
                tertiary: 'white',
                tertiaryOpacity: 0.275
            },
            gradientStops: {
                orange: {
                    startOffset: '0',
                    startOpacity: 1,
                    middleOffset: '0.65',
                    middleOpacity: 0.85,
                    endOffset: '1',
                    endOpacity: 0
                },
                blue: {
                    startOffset: '0',
                    startOpacity: 0.75,
                    middleOffset: '0.4',
                    middleOpacity: 0.5,
                    whiteOffset: '0.7',
                    whiteOpacity: 0.6,
                    endOffset: '1',
                    endOpacity: 0
                }
            }
        },
        productOfTheMonth: {
            mobileBackground: ProductOfTheMonthMobileBackground,
            showBee: false,
            colors: {
                circleMain: '#FFCC00',
                circleBackRight: '#007F95',
                orangeGradient: '#FF9800',
                blueGradient: '#007F95',
                tertiary: '#FFFFFF',
                tertiaryOpacity: 0.275
            },
            gradientStops: {
                orange: {
                    startOffset: '0',
                    startOpacity: 1,
                    middleOffset: '0.15',
                    middleOpacity: 0.85,
                    endOffset: '1',
                    endOpacity: 0
                },
                blue: {
                    startOffset: '0',
                    startOpacity: 0.75,
                    middleOffset: '0.6',
                    middleOpacity: 0.5,
                    whiteOffset: '0.8',
                    whiteOpacity: 0.6,
                    endOffset: '1',
                    endOpacity: 0
                }
            }
        },
        training: {
            mobileBackground: MobileBackground,
            showBee: false,
            colors: {
                circleMain: '#B80E80',
                circleBackRight: '#B80E80',
                orangeGradient: '#FF9800',
                blueGradient: '#007F95',
                tertiary: 'white',
                tertiaryOpacity: 0.275
            },
            gradientStops: {
                orange: {
                    startOffset: '0',
                    startOpacity: 1,
                    middleOffset: '0.65',
                    middleOpacity: 0.85,
                    endOffset: '1',
                    endOpacity: 0
                },
                blue: {
                    startOffset: '0',
                    startOpacity: 0.75,
                    middleOffset: '0.4',
                    middleOpacity: 0.5,
                    whiteOffset: '0.7',
                    whiteOpacity: 0.6,
                    endOffset: '1',
                    endOpacity: 0
                }
            }
        }
    };

    const variantConfig = computed(() => backgroundVariants[props.variant]);
    const showBee = computed(() => variantConfig.value.showBee);
    const ids = {
        circleMainGradient: `circleMainGradient-${uniqueId}`,
        circleBackRightGradient: `circleBackRightGradient-${uniqueId}`,
        academyOrangeGradient: `academyOrangeGradient-${uniqueId}`,
        academyBlueGradient: `academyBlueGradient-${uniqueId}`,
        academyBackgroundClip: `academyBackgroundClip-${uniqueId}`
    };

    const parallaxGroup = useTemplateRef<SVGGElement>('parallaxGroup');
    const circleMain = useTemplateRef<SVGGElement>('circleMain');
    const circleBackLeft = useTemplateRef<SVGGElement>('circleBackLeft');
    const circleBackRight = useTemplateRef<SVGGElement>('circleBackRight');
    const beeParallax = useTemplateRef<SVGGElement>('beeParallax');
    const beeMobile = useTemplateRef<HTMLImageElement>('beeMobile');

    let mm: ReturnType<typeof gsap.matchMedia> | null = null;

    onMounted(() => {
        mm = gsap.matchMedia();

        mm.add(
            {
                desktop: '(min-width: 1025px)',
                tablet: '(min-width: 768px) and (max-width: 1024.98px)'
            },
            (ctx) => {
                const { desktop, tablet } = ctx.conditions ?? {};

                let layers: {
                    el: Element | null;
                    damp: number;
                    baseX: number;
                    baseY: number;
                }[] = [];

                if (desktop) {
                    gsap.set(circleMain.value, {
                        transformOrigin: '50% 50%',
                        x: 0,
                        y: 0,
                        xPercent: 0,
                        yPercent: 0,
                        scale: 0.9
                    });

                    gsap.set(circleBackLeft.value, {
                        transformOrigin: '50% 50%',
                        x: 0,
                        y: 0,
                        xPercent: 20,
                        yPercent: 20,
                        scale: 0.5
                    });

                    gsap.set(circleBackRight.value, {
                        transformOrigin: '50% 50%',
                        x: 0,
                        y: 0,
                        scale: 1
                    });

                    if (beeParallax.value) {
                        gsap.set(beeParallax.value, {
                            transformOrigin: '50% 50%',
                            x: 0,
                            y: 0,
                            scale: 1
                        });
                    }

                    layers = [
                        { el: circleBackLeft.value, damp: 0.03, baseX: 0, baseY: 0 },
                        { el: circleBackRight.value, damp: 0.05, baseX: 0, baseY: 0 },
                        { el: circleMain.value, damp: 0.07, baseX: 0, baseY: 0 }
                    ];

                    if (beeParallax.value) {
                        layers.push({ el: beeParallax.value, damp: 0.1, baseX: 0, baseY: 0 });
                    }
                }

                if (tablet) {
                    gsap.set(circleMain.value, {
                        transformOrigin: '50% 50%',
                        x: 280,
                        y: 160,
                        xPercent: 0,
                        yPercent: 0,
                        scale: 0.45
                    });

                    gsap.set(circleBackLeft.value, {
                        transformOrigin: '50% 50%',
                        x: 500,
                        y: 300,
                        xPercent: 0,
                        yPercent: 0,
                        scale: 0.45
                    });

                    gsap.set(circleBackRight.value, {
                        transformOrigin: '50% 50%',
                        x: -120,
                        y: -220,
                        xPercent: 0,
                        yPercent: 0,
                        scale: 0.45
                    });

                    if (beeParallax.value) {
                        gsap.set(beeParallax.value, {
                            transformOrigin: '50% 50%',
                            x: -220,
                            y: 0,
                            xPercent: 50,
                            yPercent: 0,
                            scale: 0.75
                        });
                    }

                    layers = [
                        { el: circleBackLeft.value, damp: 0.03, baseX: 500, baseY: 300 },
                        { el: circleBackRight.value, damp: 0.05, baseX: -120, baseY: -220 },
                        { el: circleMain.value, damp: 0.07, baseX: 280, baseY: 160 }
                    ];

                    if (beeParallax.value) {
                        layers.push({ el: beeParallax.value, damp: 0.1, baseX: -220, baseY: 0 });
                    }
                }

                const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

                tl.from(parallaxGroup.value, {
                    opacity: 0,
                    duration: 0.6
                }).to(
                    circleMain.value,
                    {
                        scale: '+=0.03',
                        duration: 0.7,
                        yoyo: true,
                        repeat: 1
                    },
                    '>-0.1'
                );

                const onMove = (e: MouseEvent) => {
                    const x = e.clientX - window.innerWidth / 2;
                    const y = e.clientY - window.innerHeight / 2;

                    layers.forEach(({ el, damp, baseX, baseY }, i) => {
                        if (!el) {
                            return;
                        }

                        gsap.to(el, {
                            x: baseX + x * damp,
                            y: baseY + y * damp,
                            duration: 0.2,
                            ease: 'none',
                            overwrite: 'auto',
                            delay: i * 0.01
                        });
                    });
                };

                window.addEventListener('mousemove', onMove, { passive: true });

                return () => {
                    window.removeEventListener('mousemove', onMove);
                };
            }
        );

        if (beeMobile.value) {
            gsap.set(beeMobile.value, {
                rotation: 25,
                transformOrigin: '50% 50%'
            });
            gsap.to(beeMobile.value, {
                y: '+=10',
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut'
            });
        }
    });

    onBeforeUnmount(() => {
        mm?.revert();
    });
</script>

<template>
    <div class="background animated">
        <div class="white-gradient" />

        <img
            :src="variantConfig.mobileBackground"
            class="mobile"
            alt=""
        />

        <img
            v-if="showBee"
            ref="beeMobile"
            :src="Bee"
            class="bee-mobile"
            alt=""
            aria-hidden="true"
        />

        <svg
            class="bg-svg desktop"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1920 558"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <radialGradient
                    :id="ids.circleMainGradient"
                    cx="50%"
                    cy="50%"
                    r="50%"
                >
                    <stop
                        offset="0%"
                        :stop-color="variantConfig.colors.circleMain"
                        stop-opacity="1"
                    />
                    <stop
                        offset="100%"
                        :stop-color="variantConfig.colors.circleMain"
                        stop-opacity="0.2"
                    />
                </radialGradient>

                <radialGradient
                    :id="ids.circleBackRightGradient"
                    cx="50%"
                    cy="50%"
                    r="50%"
                >
                    <stop
                        offset="0%"
                        :stop-color="variantConfig.colors.circleBackRight"
                        stop-opacity="0.5"
                    />
                    <stop
                        offset="100%"
                        :stop-color="variantConfig.colors.circleBackRight"
                        stop-opacity="0.15"
                    />
                </radialGradient>

                <linearGradient
                    :id="ids.academyOrangeGradient"
                    x1="1091.82"
                    y1="-2.39685"
                    x2="1096.54"
                    y2="643.516"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        :offset="variantConfig.gradientStops.orange.startOffset"
                        :stop-color="variantConfig.colors.orangeGradient"
                        :stop-opacity="variantConfig.gradientStops.orange.startOpacity"
                    />
                    <stop
                        :offset="variantConfig.gradientStops.orange.middleOffset"
                        :stop-color="variantConfig.colors.orangeGradient"
                        :stop-opacity="variantConfig.gradientStops.orange.middleOpacity"
                    />
                    <stop
                        :offset="variantConfig.gradientStops.orange.endOffset"
                        stop-color="white"
                        :stop-opacity="variantConfig.gradientStops.orange.endOpacity"
                    />
                </linearGradient>

                <linearGradient
                    :id="ids.academyBlueGradient"
                    x1="1087"
                    y1="1003.32"
                    x2="1087"
                    y2="196.262"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        :offset="variantConfig.gradientStops.blue.startOffset"
                        :stop-color="variantConfig.colors.blueGradient"
                        :stop-opacity="variantConfig.gradientStops.blue.startOpacity"
                    />
                    <stop
                        :offset="variantConfig.gradientStops.blue.middleOffset"
                        :stop-color="variantConfig.colors.blueGradient"
                        :stop-opacity="variantConfig.gradientStops.blue.middleOpacity"
                    />
                    <stop
                        :offset="variantConfig.gradientStops.blue.whiteOffset"
                        stop-color="#FFFFFF"
                        :stop-opacity="variantConfig.gradientStops.blue.whiteOpacity"
                    />
                    <stop
                        :offset="variantConfig.gradientStops.blue.endOffset"
                        stop-color="#FFFFFF"
                        :stop-opacity="variantConfig.gradientStops.blue.endOpacity"
                    />
                </linearGradient>

                <clipPath :id="ids.academyBackgroundClip">
                    <rect
                        width="2190"
                        height="1010"
                        transform="translate(-1 -117)"
                    />
                </clipPath>
            </defs>

            <g :clip-path="`url(#${ids.academyBackgroundClip})`">
                <path
                    d="M2189 -117H-1V621H2189V-117Z"
                    :fill="`url(#${ids.academyOrangeGradient})`"
                />
                <path
                    d="M2175 204H-1V950H2175V204Z"
                    :fill="`url(#${ids.academyBlueGradient})`"
                />

                <g
                    id="scene"
                    ref="parallaxGroup"
                >
                    <g ref="circleMain">
                        <ellipse
                            class="circle-main"
                            cx="940.9"
                            cy="289.8"
                            rx="558.4"
                            ry="558.4"
                            :fill="`url(#${ids.circleMainGradient})`"
                            opacity="0.51"
                            transform="rotate(2.5 940.9 289.8)"
                        />
                    </g>

                    <g ref="circleBackRight">
                        <ellipse
                            class="circle-secondary"
                            cx="1749.82"
                            cy="702.139"
                            rx="481.686"
                            ry="481.686"
                            :fill="`url(#${ids.circleBackRightGradient})`"
                        />
                    </g>

                    <g ref="circleBackLeft">
                        <ellipse
                            class="circle-tertiary"
                            cx="115.074"
                            cy="-324.914"
                            rx="669.2"
                            ry="669.2"
                            :fill="variantConfig.colors.tertiary"
                            :fill-opacity="variantConfig.colors.tertiaryOpacity"
                        />
                    </g>

                    <g
                        v-if="showBee"
                        ref="beeParallax"
                        class="bee-parallax"
                        transform="rotate(25 1130 235)"
                    >
                        <image
                            :href="Bee"
                            x="720"
                            y="320"
                            width="180"
                            height="180"
                        />
                    </g>
                </g>
            </g>
        </svg>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/academy/academy-hero-background-animated.scss';
</style>
